import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ColumnPaginationDots - Calm, neutral pagination indicator for mobile column swiping
 * 
 * Features:
 * - Shows 1 dot per column for 1-4 columns
 * - Uses sliding window (max 5 dots) for 5+ columns
 * - Active dot is slightly darker gray and 10-15% larger
 * - Perfect circle shape
 * - Subtle 0.15-0.2s ease animation on active change
 * - Minimal, quiet, supportive aesthetic
 */
export const ColumnPaginationDots = ({ 
  totalColumns, 
  boardRef,
  className = "" 
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const lastActiveRef = useRef(0);
  
  // Calculate which dots to show (sliding window for 5+ columns)
  const maxDots = 5;
  const showSlidingWindow = totalColumns > maxDots;
  
  const getVisibleDots = useCallback(() => {
    if (!showSlidingWindow) {
      return Array.from({ length: totalColumns }, (_, i) => i);
    }
    
    // Sliding window logic
    const halfWindow = Math.floor(maxDots / 2);
    let start = Math.max(0, activeIndex - halfWindow);
    let end = start + maxDots;
    
    // Adjust if we're near the end
    if (end > totalColumns) {
      end = totalColumns;
      start = Math.max(0, end - maxDots);
    }
    
    return Array.from({ length: end - start }, (_, i) => start + i);
  }, [activeIndex, totalColumns, showSlidingWindow]);

  // Track scroll position to determine active column
  useEffect(() => {
    if (!boardRef?.current) return;
    
    const board = boardRef.current;
    
    const handleScroll = () => {
      const scrollLeft = board.scrollLeft;
      
      // Find all column elements
      const columns = board.querySelectorAll('[data-column-index]');
      if (columns.length === 0) return;
      
      // Determine which column is most visible
      let maxVisibleColumn = 0;
      let maxVisibleArea = 0;
      
      columns.forEach((col, index) => {
        const rect = col.getBoundingClientRect();
        const boardRect = board.getBoundingClientRect();
        
        // Calculate visible width of this column
        const visibleLeft = Math.max(rect.left, boardRect.left);
        const visibleRight = Math.min(rect.right, boardRect.right);
        const visibleWidth = Math.max(0, visibleRight - visibleLeft);
        
        if (visibleWidth > maxVisibleArea) {
          maxVisibleArea = visibleWidth;
          maxVisibleColumn = index;
        }
      });
      
      if (maxVisibleColumn !== activeIndex) {
        lastActiveRef.current = activeIndex;
        setActiveIndex(maxVisibleColumn);
      }
    };
    
    board.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => board.removeEventListener('scroll', handleScroll);
  }, [boardRef, activeIndex, totalColumns]);

  // Don't show if only 1 column
  if (totalColumns <= 1) return null;

  const visibleDots = getVisibleDots();
  const isAtStart = showSlidingWindow && visibleDots[0] > 0;
  const isAtEnd = showSlidingWindow && visibleDots[visibleDots.length - 1] < totalColumns - 1;

  // Dot styling constants - calm, aqua blue aesthetic
  const inactiveColor = '#7DD3E1'; // Light aqua blue
  const activeColor = '#3DC4D0';   // Aqua blue (brand color)
  const inactiveSize = 5;          // Perfect circle
  const activeSize = 6;            // ~20% larger when active

  return (
    <div 
      className={`flex items-center justify-center gap-2 ${className}`}
      data-testid="column-pagination-dots"
    >
      {/* Left edge indicator for sliding window */}
      {isAtStart && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          style={{
            width: 3,
            height: 3,
            borderRadius: '50%',
            backgroundColor: '#7DD3E1',
          }}
        />
      )}
      
      {visibleDots.map((dotIndex) => {
        const isActive = dotIndex === activeIndex;
        const distanceFromActive = Math.abs(dotIndex - activeIndex);
        
        // Dots further from active are slightly more faded (for sliding window)
        const opacityMultiplier = showSlidingWindow 
          ? Math.max(0.4, 1 - distanceFromActive * 0.2)
          : 1;
        
        return (
          <motion.div
            key={dotIndex}
            initial={false}
            animate={{
              width: isActive ? activeSize : inactiveSize,
              height: isActive ? activeSize : inactiveSize,
              backgroundColor: isActive ? activeColor : inactiveColor,
              opacity: isActive ? 1 : (showSlidingWindow ? opacityMultiplier * 0.6 : 0.6),
            }}
            transition={{ 
              duration: 0.18,
              ease: "easeInOut"
            }}
            style={{
              borderRadius: '50%',
            }}
          />
        );
      })}
      
      {/* Right edge indicator for sliding window */}
      {isAtEnd && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          style={{
            width: 3,
            height: 3,
            borderRadius: '50%',
            backgroundColor: '#7DD3E1',
          }}
        />
      )}
    </div>
  );
};

export default ColumnPaginationDots;
